import { useMemo, useState, useEffect } from 'react';
import { FigureComposition, useFigureCompositionStore, getPanelRow } from '@/store/figure-composition';
import type { Data, Layout } from 'plotly.js';

export interface Warning {
  id: string;
  level: 'info' | 'warning';
  message: string;
}

export function useFigureWarnings(composition: FigureComposition | null): Warning[] {
  const [scaleMismatches, setScaleMismatches] = useState<Array<{ panels: string[]; axis: 'x' | 'y'; message: string }>>([]);
  const hydratePanel = useFigureCompositionStore((state) => state.hydratePanel);

  // Load scale mismatch data asynchronously
  useEffect(() => {
    if (!composition?.axesSharing?.warnOnScaleMismatch || composition.panels.length < 2) {
      setScaleMismatches([]);
      return;
    }

    let cancelled = false;

    const checkScaleMismatches = async () => {
      const gridUnitWidth = composition.grid.cols / 12;
      const mismatches: Array<{ panels: string[]; axis: 'x' | 'y'; message: string }> = [];

      // Group panels by column/row
      const panelsByColumn = new Map<number, typeof composition.panels>();
      const panelsByRow = new Map<number, typeof composition.panels>();

      composition.panels.forEach((panel) => {
        if (!panel.plotId) return;
        const col = Math.floor(panel.x / gridUnitWidth);
        const row = getPanelRow(panel);

        if (!panelsByColumn.has(col)) {
          panelsByColumn.set(col, []);
        }
        panelsByColumn.get(col)!.push(panel);

        if (!panelsByRow.has(row)) {
          panelsByRow.set(row, []);
        }
        panelsByRow.get(row)!.push(panel);
      });

      // Check X-axis mismatches for columns
      if (composition.axesSharing.shareXAcrossColumns) {
        for (const [col, panels] of panelsByColumn.entries()) {
          if (panels.length < 2) continue;

          try {
            const panelData = await Promise.all(
              panels.map((p) => hydratePanel(p, composition).catch(() => null))
            );

            const validPanels = panelData.filter(
              (d): d is { data: Data[]; layout: Partial<Layout> } => d !== null
            );
            if (validPanels.length < 2) continue;

            const xRanges = validPanels
              .map(({ layout }) => {
                const xaxis = layout.xaxis as any;
                if (xaxis && Array.isArray(xaxis.range) && xaxis.range.length === 2) {
                  return xaxis.range as [number, number];
                }
                return null;
              })
              .filter((r): r is [number, number] => r !== null);

            if (xRanges.length >= 2) {
              // Check if ranges differ significantly (>10% difference)
              const ranges = xRanges.map((r) => r[1] - r[0]);
              const minRange = Math.min(...ranges);
              const maxRange = Math.max(...ranges);
              const diffPercent = ((maxRange - minRange) / minRange) * 100;

              if (diffPercent > 10) {
                const panelLabels = panels.map((p) => p.label).join(' and ');
                mismatches.push({
                  panels: panels.map((p) => p.label),
                  axis: 'x',
                  message: `Panels ${panelLabels} in column ${col + 1} have different X-axis ranges (${diffPercent.toFixed(1)}% difference).`,
                });
              }
            }
          } catch (error) {
            // Skip this column if loading fails
          }
        }
      }

      // Check Y-axis mismatches for rows
      if (composition.axesSharing.shareYAcrossRows) {
        for (const [row, panels] of panelsByRow.entries()) {
          if (panels.length < 2) continue;

          try {
            const panelData = await Promise.all(
              panels.map((p) => hydratePanel(p, composition).catch(() => null))
            );

            const validPanels = panelData.filter(
              (d): d is { data: Data[]; layout: Partial<Layout> } => d !== null
            );
            if (validPanels.length < 2) continue;

            const yRanges = validPanels
              .map(({ layout }) => {
                const yaxis = layout.yaxis as any;
                if (yaxis && Array.isArray(yaxis.range) && yaxis.range.length === 2) {
                  return yaxis.range as [number, number];
                }
                return null;
              })
              .filter((r): r is [number, number] => r !== null);

            if (yRanges.length >= 2) {
              const ranges = yRanges.map((r) => r[1] - r[0]);
              const minRange = Math.min(...ranges);
              const maxRange = Math.max(...ranges);
              const diffPercent = ((maxRange - minRange) / minRange) * 100;

              if (diffPercent > 10) {
                const panelLabels = panels.map((p) => p.label).join(' and ');
                mismatches.push({
                  panels: panels.map((p) => p.label),
                  axis: 'y',
                  message: `Panels ${panelLabels} in row ${row + 1} have different Y-axis ranges (${diffPercent.toFixed(1)}% difference).`,
                });
              }
            }
          } catch (error) {
            // Skip this row if loading fails
          }
        }
      }

      if (!cancelled) {
        setScaleMismatches(mismatches);
      }
    };

    checkScaleMismatches();

    return () => {
      cancelled = true;
    };
  }, [composition?.id, composition?.axesSharing?.warnOnScaleMismatch, composition?.axesSharing?.shareXAcrossColumns, composition?.axesSharing?.shareYAcrossRows, composition?.panels, hydratePanel]);

  return useMemo(() => {
    if (!composition) return [];

    const warnings: Warning[] = [];

    // 1. Font size warnings
    const canvasWidthMm = composition.canvasSize.width;
    const canvasHeightMm = composition.canvasSize.height;
    const baseFontSizePt = composition.theme.fontSize;

    // Approximate calculation: assume export at 300 DPI
    const mmToPxAt300Dpi = (mm: number) => (mm / 25.4) * 300;
    const canvasWidthPx = mmToPxAt300Dpi(canvasWidthMm);
    const canvasHeightPx = mmToPxAt300Dpi(canvasHeightMm);

    const scaleFactor = Math.min(canvasWidthPx / 800, canvasHeightPx / 600);
    const effectiveFontSize = baseFontSizePt * scaleFactor;

    if (effectiveFontSize < 6) {
      warnings.push({
        id: 'font-size-small',
        level: 'warning',
        message: `Some labels may render below ~6 pt at the current size (effective: ~${effectiveFontSize.toFixed(1)} pt).`,
      });
    }

    // 2. DPI warnings for plot panels
    // Check if canvas size is too small for high-DPI print (300 DPI)
    // (canvasWidthMm and canvasHeightMm are already declared above)
    // Typical print sizes: A4 is 210x297mm, single column is ~85mm wide
    // For 300 DPI print, we want at least ~85mm width for single column
    // Very small canvases (< 50mm) will have low effective DPI
    const minRecommendedWidth = 50; // mm
    const minRecommendedHeight = 50; // mm
    
    if (canvasWidthMm < minRecommendedWidth || canvasHeightMm < minRecommendedHeight) {
      warnings.push({
        id: 'dpi-low-canvas-size',
        level: 'warning',
        message: `Canvas size (${canvasWidthMm.toFixed(0)}×${canvasHeightMm.toFixed(0)}mm) may result in low resolution for print. Recommended minimum: ${minRecommendedWidth}×${minRecommendedHeight}mm for 300 DPI.`,
      });
    }
    
    // Check if panels have plots assigned (for future DPI checks)
    const panelsWithPlots = composition.panels.filter(p => p.plotId);
    if (panelsWithPlots.length === 0) {
      // No plots assigned yet - skip DPI checks
    }

    // 3. Scale mismatch warnings (from async check)
    scaleMismatches.forEach((mismatch) => {
      warnings.push({
        id: `scale-mismatch-${mismatch.axis}-${mismatch.panels.join('-')}`,
        level: 'warning',
        message: mismatch.message,
      });
    });

    return warnings;
  }, [composition, scaleMismatches]);
}

